import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Loader from '../../component/common/Loader';
import Breadcrumb from '../../component/common/Breadcrumb';
import Topbar from '../PulseSurvey/Topbar';
import { errorToast, successToast, isSuperAdmin } from '../../utils/helper';
import { createGameAchievement, getAchievementTypes } from '../../api/achievementApi';

const getPages = (isAdmin, slug) => {
  if (isAdmin) {
    return [
      { name: 'Achievements', href: '/achievements' },
      { name: 'Create Achievement', href: '#', current: true }
    ];
  } else {
    return [
      { name: 'Achievements', href: `/${slug}/achievements` },
      { name: 'Create Achievement', href: '#', current: true }
    ];
  }
};

const CreateAchievement = () => {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const { slug } = userData;
  const isAdmin = isSuperAdmin();

  const [loader, setLoader] = useState(false);
  const [types, setTypes] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    type: '',
    targetValue: '',
    rewards: ''
  });

  useEffect(() => {
    fetchAchievementTypes();
  }, []);

  const fetchAchievementTypes = async () => {
    try {
      const response = await getAchievementTypes();
      console.log('Achievement Types Response:', response);
      if (response?.data) {
        setTypes(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching achievement types:', error);
      errorToast('Failed to load achievement types');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      errorToast('Please enter a title');
      return;
    }
    if (!formData.description.trim()) {
      errorToast('Please enter a description');
      return;
    }
    if (!formData.type) {
      errorToast('Please select an achievement type');
      return;
    }
    if (!formData.targetValue || formData.targetValue <= 0) {
      errorToast('Please enter a valid target value');
      return;
    }
    if (!formData.rewards || formData.rewards <= 0) {
      errorToast('Please enter valid rewards');
      return;
    }

    setLoader(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        icon: formData.icon || 'icon_url',
        type: parseInt(formData.type),
        targetValue: parseInt(formData.targetValue),
        rewards: parseInt(formData.rewards)
      };

      const response = await createGameAchievement(payload);
      console.log('Create Achievement Response:', response);

      if (response) {
        successToast('Achievement created successfully');
        navigate('/achievements');
      }
    } catch (error) {
      console.error('Error creating achievement:', error);
      errorToast(error?.message || 'Failed to create achievement');
    } finally {
      setLoader(false);
    }
  };

  const handleCancel = () => {
    navigate('/achievements');
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Create Achievement | Shoorah Admin</title>
      </Helmet>

      {loader && <Loader />}

      <div className="ml-2">
        <div className="flex justify-between items-center py-4">
          <Topbar title={'Create Achievement'} />
        </div>

        <Breadcrumb pageList={getPages(isAdmin, slug)} className="lg:mt-8" />

        {/* Form Section */}
        <div className="mt-12">
          <div className="bg-white p-6 dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Title */}
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white"
                    placeholder="Enter achievement title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white"
                    placeholder="Enter achievement description"
                  />
                </div>

                {/* Type */}
                <div>
                  <label
                    htmlFor="type"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Achievement Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white"
                  >
                    <option value="">Select Type</option>
                    {types.map((type) => (
                      <option key={type.id || type._id} value={type.id || type._id}>
                        {type.name || type.typeName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target Value */}
                <div>
                  <label
                    htmlFor="targetValue"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Target Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="targetValue"
                    name="targetValue"
                    value={formData.targetValue}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white"
                    placeholder="Enter target value"
                  />
                </div>

                {/* Rewards */}
                <div>
                  <label
                    htmlFor="rewards"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Rewards <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="rewards"
                    name="rewards"
                    value={formData.rewards}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white"
                    placeholder="Enter reward points"
                  />
                </div>

                {/* Icon URL */}
                <div>
                  <label
                    htmlFor="icon"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Icon URL
                  </label>
                  <input
                    type="text"
                    id="icon"
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent dark:bg-shoorah-darkBgColor dark:text-white"
                    placeholder="Enter icon URL (optional)"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 mt-8">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loader}
                  className="btn-primary"
                >
                  {loader ? 'Creating...' : 'Create Achievement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAchievement;
